import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLang, useTextDomainContext, useUser } from "@/app/providers";
import {
  GENERATE_ENERGY_STATEMENT_FILE,
  useClimateData,
  useGetGroups,
} from "@/hooks";
import {
  FilterBar1,
  AreaChartBox,
  TableView1,
  ToolbarDropdown,
} from "@/components";
import moment from "moment";
import {
  exportableStatements,
  formatDateForAPI,
  formatDateYear,
  getPreviousYearDate,
  segmentList,
  toBenchmarkDataClimate,
  toLocaleNumber,
} from "@/utils";
import { useTheme } from "@mui/material";
import type { GridRowClassNameParams } from "@mui/x-data-grid";
import { TfiImport } from "react-icons/tfi";
import { enqueueSnackbar } from "notistack";
import { useLazyQuery } from "@apollo/client/react";

export const Carbon1 = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const isSuperAdmin = user?.roles?.includes("super-admin");
  const segments = segmentList();
  let {
    currentTeam: customer,
    currentSubsidiary: group,
    currentTeamId,
  } = user || {};
  const locale = useLang();
  const theme = useTheme();
  if (!customer) {
    customer = "all";
  }
  if (!group) {
    group = "All";
  }

  //region React Hooks (useState)
  const [building, setBuilding] = useState("all");
  const [meter, setMeter] = useState("all");
  const [energyType, setEnergyType] = useState("electricity");
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(0, 1); // January 1st of current year
    return date.toISOString();
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString());
  // const [segment, setSegment] = useState("year");
  const [shortSegment, setShortSegment] = useState("m");
  // const [chartData, setChartData] = useState([]);
  // const [fileNameForExport, setFileNameForExport] = useState(
  //   "carbon-emissions-export",
  // );
  // const [tableData, setTableData] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [country, setCountry] = useState(null);
  //endregion  React Hooks (useState)

  //region Data
  // const filters = {
  //   customer,
  //   group,
  //   address: building,
  //   meteringPointId: meter,
  //   meterType: energyType,
  //   fromDate: formatDateForAPI(fromDate),
  //   toDate: formatDateForAPI(toDate),
  //   resolution: shortSegment,
  //   fileType: "csv",
  // };
  // const { data: metersData, loading } = useClimateData(filters);

  const thisYearFilter = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: formatDateForAPI(fromDate),
    toDate: formatDateForAPI(toDate),
    resolution: shortSegment,
    country,
  };

  const previousYearFilter = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: getPreviousYearDate(thisYearFilter.fromDate),
    toDate: getPreviousYearDate(thisYearFilter.toDate),
    resolution: shortSegment,
    country,
  };

  const {
    data: metersDataThisYear,
    total: totalThisYear,
    loading: loadingThisYear,
  } = useClimateData(thisYearFilter);

  const {
    data: metersDataPreviousYear,
    total: totalPreviousYear,
    loading: loadingPreviousYear,
  } = useClimateData(previousYearFilter);

  const [addExcelInQueue] = useLazyQuery(GENERATE_ENERGY_STATEMENT_FILE);

  const { subsidiary } = useGetGroups({
    customerId: currentTeamId,
    customerName: customer,
  });

  // const [addExcelInQueue] = useLazyQuery(GENERATE_ENERGY_STATEMENT_FILE, {
  //   variables: {
  //     meterType: energyType,
  //     customer: customer,
  //     group: group,
  //     year: new Date(fromDate).getFullYear(),
  //     isClimate: true,
  //   },
  // });

  const currentYearNum = new Date().getFullYear();
  const thisYear =
    formatDateYear(thisYearFilter.fromDate) || String(currentYearNum);
  const previousYear =
    formatDateYear(previousYearFilter.fromDate) || String(currentYearNum - 1);
  //endregion Data

  //region React Hooks (useEffect)

  const tableData = useMemo(() => {
    return toBenchmarkDataClimate(
      shortSegment,
      [
        {
          data: metersDataThisYear,
          total: totalThisYear,
        },
        {
          data: metersDataPreviousYear,
          total: totalPreviousYear,
        },
      ],
      gettext,
      true,
    );
  }, [
    shortSegment,
    metersDataThisYear,
    totalThisYear,
    metersDataPreviousYear,
    totalPreviousYear,
    gettext,
  ]);

  const chartData = useMemo(() => {
    return toBenchmarkDataClimate(
      shortSegment,
      [
        {
          data: metersDataThisYear,
          total: totalThisYear,
        },
        {
          data: metersDataPreviousYear,
          total: totalPreviousYear,
        },
      ],
      gettext,
      false,
    );
  }, [
    shortSegment,
    metersDataThisYear,
    totalThisYear,
    metersDataPreviousYear,
    totalPreviousYear,
  ]);

  // useEffect(() => {
  //   const calculatedTableData = toBenchmarkDataClimate(
  //     shortSegment,
  //     [
  //       {
  //         data: metersDataThisYear,
  //         total: totalThisYear,
  //       },
  //       {
  //         data: metersDataPreviousYear,
  //         total: totalPreviousYear,
  //       },
  //     ],
  //     gettext,
  //     true,
  //   );
  //
  //   const calculatedChartData = toBenchmarkDataClimate(
  //     shortSegment,
  //     [
  //       {
  //         data: metersDataThisYear,
  //         total: totalThisYear,
  //       },
  //       {
  //         data: metersDataPreviousYear,
  //         total: totalPreviousYear,
  //       },
  //     ],
  //     false,
  //   );
  //
  //   if (!isEqual(tableData, calculatedTableData)) {
  //     setTableData(calculatedTableData);
  //   }
  //
  //   if (!isEqual(chartData, calculatedChartData)) {
  //     setChartData(calculatedChartData);
  //   }
  //
  //   // console.log(calculatedTableData);
  // }, [
  //   chartData,
  //   gettext,
  //   metersDataPreviousYear,
  //   metersDataThisYear,
  //   shortSegment,
  //   tableData,
  //   totalPreviousYear,
  //   totalThisYear,
  // ]);

  // useEffect(() => {
  //   setFileNameForExport(
  //     urlFriendlyString(
  //       "carbon-emissions-" +
  //         building +
  //         "-" +
  //         meter +
  //         "-" +
  //         energyType +
  //         "-" +
  //         segment +
  //         "-" +
  //         fromDate +
  //         "-" +
  //         toDate,
  //     ),
  //   );
  // }, [building, energyType, fromDate, meter, segment, toDate]);

  useEffect(() => {
    const fetchData = async () => {
      const customerName = group.toLowerCase() === "all" ? customer : group;
      let fileNamePostfix = group.toLowerCase() === "all" ? "-all" : "";
      if (subsidiary.length < 2) {
        fileNamePostfix = "-all";
      }
      const res = [];

      const res1 = await exportableStatements(
        "climate-statement",
        customerName,
        fileNamePostfix,
        energyType,
        fromDate,
        gettext,
      );

      if (isSuperAdmin) {
        // const res2 = await exportableStatementsTest(
        //   "climate-statement",
        //   customerName,
        //   fileNamePostfix,
        //   energyType,
        //   fromDate,
        //   gettext,
        // );

        // res.push(...res1, ...res2);
        res.push(...res1);

        const obj = {
          label: gettext("Generate Climate Report"),
          key: "generate-report",
        };

        res.push(obj);
      } else {
        res.push(...res1);
      }

      setExportData(res);
    };
    fetchData().then();
  }, [
    customer,
    energyType,
    fromDate,
    gettext,
    group,
    isSuperAdmin,
    subsidiary.length,
  ]);

  // useEffect(() => {
  //   if (metersData) {
  //     const chartDateTemp = meterApiDataToBarChartData(
  //       metersData,
  //       shortSegment,
  //     );
  //     if (!isEqual(chartDateTemp, chartData)) {
  //       setChartData(meterApiDataToBarChartData(metersData, shortSegment));
  //     }
  //   }
  // }, [chartData, metersData, shortSegment]);

  //endregion React Hooks (useEffect)

  //region For Table

  const columnGroupingModel = [
    // {
    //   groupId: "ts",
    //   children: [{ field: "ts" }],
    // },
    {
      groupId: gettext("Consumption (kWh)"),
      headerAlign: "center",
      headerClassName: "borderLeft",
      children: [
        { field: `consumptionV_${previousYear}` },
        { field: `consumptionV_${thisYear}` },
      ],
    },
    {
      groupId: gettext("Carbon Footprint (g CO₂e/kWh)"),
      headerAlign: "center",
      headerClassName: "borderLeft",
      children: [
        { field: `conversionRate_${previousYear}` },
        { field: `conversionRate_${thisYear}` },
      ],
    },
    {
      groupId: gettext("Carbon Emissions (Kg CO₂e)"),
      headerAlign: "center",
      headerClassName: "borderLeft",
      children: [
        { field: `co2_${previousYear}` },
        { field: `co2_${thisYear}` },
      ],
    },
  ];

  const columns = [
    {
      field: "ts",
      headerName: gettext("Date"),
      flex: 1,
      // type: "date",
      headerAlign: "center",
      // valueGetter: (value) => {
      //   let returnValue = "";
      //   if (value) {
      //     if (value === "Summary") {
      //       returnValue = value;
      //     } else {
      //       returnValue = new Date(value);
      //     }
      //   }
      //   return returnValue;
      // },
      // valueFormatter: (value) => {
      //   let returnValue = "";
      //   if (value) {
      //     if (value === "Summary") {
      //       returnValue = value;
      //     } else {
      //       // returnValue = capitalizeFirstLetter(moment(value).format(segments[segment].dateFormat));
      //       returnValue = formatDateBySegment(value, segment);
      //     }
      //   }
      //   return returnValue;
      // },
    },
    {
      field: `consumptionV_${previousYear}`,
      headerName: previousYear,
      flex: 1,
      type: "number",
      headerAlign: "center",
      headerClassName: "borderLeft",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: `consumptionV_${thisYear}`,
      headerName: thisYear,
      flex: 1,
      type: "number",
      headerAlign: "center",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: `conversionRate_${previousYear}`,
      headerName: previousYear,
      flex: 1,
      type: "number",
      headerAlign: "center",
      headerClassName: "borderLeft",
      valueFormatter: (value: number | string) =>
        value === "" ? "" : toLocaleNumber(locale, value, 2),
    },
    {
      field: `conversionRate_${thisYear}`,
      headerName: thisYear,
      flex: 1,
      type: "number",
      headerAlign: "center",
      valueFormatter: (value: number | string) =>
        value === "" ? "" : toLocaleNumber(locale, value, 2),
    },
    {
      field: `co2_${previousYear}`,
      headerName: previousYear,
      flex: 1,
      type: "number",
      headerAlign: "center",
      headerClassName: "borderLeft",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: `co2_${thisYear}`,
      headerName: thisYear,
      flex: 1,
      type: "number",
      headerAlign: "center",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    // {
    //   field: "consumptionCost",
    //   headerName: gettext("Cost (DKK)"),
    //   flex: 1,
    //   type: "number"
    // }
  ];

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    // columns: {
    //   columnVisibilityModel: {
    //     id: false,
    //   }
    // }
  };

  const postToolbar = () => {
    return (
      <ToolbarDropdown
        data={exportData}
        label={gettext("Reports")}
        onClick={handleClickCustomDropdownOption}
        endIcon={<TfiImport size={14} />}
      />
    );
  };
  //endregion For Table

  const handleGetSelectedValues = (selectedValues: {
    building?: { value?: string };
    meter?: { value?: string };
    energyType?: { value?: string };
    date?: { startDate?: Date; endDate?: Date };
    segment?: keyof typeof segments;
    country?: string | null;
  }) => {
    if (selectedValues?.building?.value) {
      setBuilding(selectedValues.building.value);
    }

    if (selectedValues?.meter?.value) {
      setMeter(selectedValues.meter.value);
    }

    if (selectedValues?.energyType?.value) {
      setEnergyType(selectedValues.energyType.value);
    }

    if (selectedValues?.date?.startDate) {
      setFromDate(selectedValues.date.startDate.toISOString());
    }

    if (selectedValues?.date?.endDate) {
      setToDate(selectedValues.date.endDate.toISOString());
    }

    if (selectedValues?.segment) {
      // setSegment(selectedValues.segment);
      setShortSegment(segments[selectedValues.segment].shortSegment);
      if (
        selectedValues.segment === "day" &&
        moment(selectedValues?.date?.startDate).format("YYYY-MM-DD") !==
          moment(selectedValues?.date?.endDate).format("YYYY-MM-DD")
      ) {
        // setSegment("month");
        setShortSegment(segments["month"].shortSegment);
      }
    }

    if (selectedValues?.country) {
      setCountry(selectedValues.country);
    }
  };

  const handleClickCustomDropdownOption = (obj: {
    key: string;
    label?: string;
    url?: string;
  }) => {
    let renamedGroup = group;
    if (subsidiary.length < 2) {
      renamedGroup = "all";
    }
    switch (obj.key) {
      case "generate-report":
        addExcelInQueue({
          variables: {
            meterType: energyType,
            customer: customer,
            group: renamedGroup,
            year: new Date(fromDate).getFullYear(),
            isClimate: true,
          },
        }).then(({ data }: any) => {
          if (data.generateEnergyStatementFile) {
            enqueueSnackbar(
              gettext(
                "The system is now creating the report, and you can download it shortly by clicking Download.",
              ),
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(
              gettext(
                "An error occurred while the system was generating the report. Please try again. You can contact support If this continues.",
              ),
              { variant: "error" },
            );
          }
        });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <FilterBar1
        getSelectedValues={handleGetSelectedValues}
        currentTeam={customer}
        currentSubsidiary={group}
        showCountry
        showBuilding
        showMeter
        showEnergyType
        showDateSingle
        showDateSingleHourly
        showDateSingleDaily
        showDateSingleMonthly
        // showDateMultiple
        showBuildingAll
        showMeterAll
        // showEnergyTypeAll
      />
      <br />

      {/*<BarChartBox*/}
      {/*  loading={loadingThisYear || loadingPreviousYear}*/}
      {/*  title={gettext("Kg of CO₂e")}*/}
      {/*  chartId={"co2"}*/}
      {/*  data={{*/}
      {/*    dataset: chartData,*/}
      {/*    series: [*/}
      {/*      {*/}
      {/*        dataKey: "co2",*/}
      {/*        label: gettext("Consumption"),*/}
      {/*        color: (theme.palette as any).chart.electricity,*/}
      {/*        unit: gettext("Kilogram"),*/}
      {/*      },*/}
      {/*    ],*/}
      {/*  }}*/}
      {/*/>*/}

      <AreaChartBox
        loading={loadingThisYear || loadingPreviousYear}
        title={gettext("Carbon Emissions (Kg CO₂e)")}
        chartId={"co2"}
        data={{
          dataset: chartData,
          series: [
            {
              dataKey: `co2_${previousYear}`,
              label: previousYear.toString(),
              color: (theme.palette as any).chart.year2,
              unit: gettext("Kg CO₂e"),
            },
            {
              dataKey: `co2_${thisYear}`,
              label: thisYear.toString(),
              color: (theme.palette as any).chart.year1,
              unit: gettext("Kg CO₂e"),
            },
          ],
        }}
      />
      <br />

      {/*<BarChartBox*/}
      {/*  loading={loadingThisYear || loadingPreviousYear}*/}
      {/*  title={gettext("Conversion rate in g CO₂e/kWh")}*/}
      {/*  chartId={"conversion"}*/}
      {/*  data={{*/}
      {/*    dataset: chartData,*/}
      {/*    series: [*/}
      {/*      {*/}
      {/*        dataKey: "conversionRate",*/}
      {/*        label: gettext("Consumption"),*/}
      {/*        color: (theme.palette as any).chart.electricity,*/}
      {/*        unit: gettext("g CO₂/kWh"),*/}
      {/*      },*/}
      {/*    ],*/}
      {/*  }}*/}
      {/*/>*/}

      <AreaChartBox
        loading={loadingThisYear || loadingPreviousYear}
        title={gettext("Carbon Footprint (g CO₂e/kWh)")}
        chartId={"conversion"}
        data={{
          dataset: chartData,
          series: [
            {
              dataKey: `conversionRate_${previousYear}`,
              label: previousYear.toString(),
              color: (theme.palette as any).chart.year2,
              unit: gettext("g CO₂e/kWh"),
            },
            {
              dataKey: `conversionRate_${thisYear}`,
              label: thisYear.toString(),
              color: (theme.palette as any).chart.year1,
              unit: gettext("g CO₂e/kWh"),
            },
          ],
        }}
      />
      <br />

      {/*<BarChartBox*/}
      {/*  loading={loadingThisYear || loadingPreviousYear}*/}
      {/*  title={gettext("Consumption measured in kWh")}*/}
      {/*  chartId={"consumption"}*/}
      {/*  data={{*/}
      {/*    dataset: chartData,*/}
      {/*    series: [*/}
      {/*      {*/}
      {/*        dataKey: "consumptionV",*/}
      {/*        label: gettext("Consumption"),*/}
      {/*        color: (theme.palette as any).chart.electricity,*/}
      {/*        unit: gettext("kWh"),*/}
      {/*      },*/}
      {/*    ],*/}
      {/*  }}*/}
      {/*/>*/}

      <AreaChartBox
        loading={loadingThisYear || loadingPreviousYear}
        title={gettext("Consumption (kWh)")}
        chartId={"consumption"}
        data={{
          dataset: chartData,
          series: [
            {
              dataKey: `consumptionV_${previousYear}`,
              label: previousYear.toString(),
              color: (theme.palette as any).chart.year2,
              unit: gettext("kWh"),
            },
            {
              dataKey: `consumptionV_${thisYear}`,
              label: thisYear.toString(),
              color: (theme.palette as any).chart.year1,
              unit: gettext("kWh"),
            },
          ],
        }}
      />

      <br />

      <TableView1
        title={gettext("Consumption and CO₂e")}
        rows={tableData}
        columns={columns}
        loading={loadingThisYear || loadingPreviousYear}
        initialState={initialState}
        postToolbar={postToolbar()}
        showGridToolbar
        // showGridToolbarCustomExport
        disableColumnSorting
        disableColumnSelector
        // exportData={exportData}
        columnGroupingModel={columnGroupingModel}
        getRowClassName={(params: GridRowClassNameParams) => {
          return params.row.id === "Total" ? "total-row" : "";
        }}
      />

      {energyType === "gas" && (
        <>
          <br />
          <p>
            {gettext("The 2025 and 2026")}{" "}
            <a
              href="https://evida.dk/co2-faktor"
              target="_blank"
              rel="noopener noreferrer"
            >
              {gettext("conversion rates")}
            </a>{" "}
            {gettext(
              "have not yet been released, so we use 2024 for those years until we have data.",
            )}
          </p>
        </>
      )}
    </>
  );
};
